import { useMemo } from 'react';

import { calculateCompletelyVisibleCoreRange, calculateViewportVisibleCoreRange } from '../helpers/viewportFunctions';
import { LengthRange } from '../types/lengthRange';

export interface CoreLanesProps {
	viewPortWidth?: number;
	viewPortHeight?: number;
	viewPortResolution: number;
	viewPortDrilledCoreRange?: LengthRange;
	visualDrillHoleStartOffset?: number;
	totalDrilledCoreRange: LengthRange;
}

const useVisibleCoreLanes = ({
	viewPortWidth,
	viewPortHeight,
	viewPortResolution,
	viewPortDrilledCoreRange,
	visualDrillHoleStartOffset,
	totalDrilledCoreRange,
}: CoreLanesProps): {
	visibleCoreLanesRanges: LengthRange[];
	viewportVisibleLanesRange: LengthRange;
	completelyVisibleLanesRange: LengthRange;
} => {
	const completelyVisibleLanesRange = useMemo<LengthRange>(
		() =>
			calculateCompletelyVisibleCoreRange(
				totalDrilledCoreRange,
				viewPortResolution,
				viewPortWidth,
				viewPortHeight,
				viewPortDrilledCoreRange,
				visualDrillHoleStartOffset,
			),
		[
			viewPortWidth,
			totalDrilledCoreRange,
			viewPortHeight,
			viewPortDrilledCoreRange,
			viewPortResolution,
			visualDrillHoleStartOffset,
		],
	);

	const viewportVisibleLanesRange = useMemo<LengthRange>(
		() =>
			calculateViewportVisibleCoreRange(
				totalDrilledCoreRange,
				viewPortResolution,
				viewPortWidth,
				viewPortHeight,
				viewPortDrilledCoreRange,
				visualDrillHoleStartOffset,
			),
		[
			viewPortWidth,
			totalDrilledCoreRange,
			viewPortHeight,
			viewPortDrilledCoreRange,
			viewPortResolution,
			visualDrillHoleStartOffset,
		],
	);

	const visibleCoreLanesRanges = useMemo<LengthRange[]>(() => {
		if (!viewPortWidth) return [];
		if (!viewPortHeight) return [];
		if (!viewPortDrilledCoreRange) return [];
		if (viewPortDrilledCoreRange.depthTo < viewPortDrilledCoreRange.depthFrom) return [];
		const returnLanes: LengthRange[] = [];
		const rowLength = viewPortWidth / viewPortResolution;
		let currentStart = totalDrilledCoreRange.depthFrom - (visualDrillHoleStartOffset ?? 0) - rowLength;
		const endRows = Math.min(
			viewPortDrilledCoreRange.depthTo + rowLength + (visualDrillHoleStartOffset ?? 0),
			totalDrilledCoreRange.depthTo,
		);
		while (currentStart < endRows) {
			//If we haven't reached the start of our viewport, increment and carry on
			if (currentStart < viewPortDrilledCoreRange.depthFrom - rowLength) {
				currentStart += rowLength;
				continue;
			}

			// New row, add.
			returnLanes.push({ depthFrom: currentStart, depthTo: (currentStart += rowLength) });
		}
		return returnLanes;
	}, [
		viewPortWidth,
		viewPortHeight,
		viewPortDrilledCoreRange,
		totalDrilledCoreRange.depthFrom,
		totalDrilledCoreRange.depthTo,
		visualDrillHoleStartOffset,
		viewPortResolution,
	]);

	return {
		visibleCoreLanesRanges,
		viewportVisibleLanesRange,
		completelyVisibleLanesRange,
	};
};

export default useVisibleCoreLanes;
